<template>
  <section class="allies-view section_padding">
    <div class="container">
      <header class="allies-view__header">
        <div>
          <h1>{{ t("allies.title") }}</h1>
          <p class="allies-view__subtitle">
            {{ t("allies.subtitle") }}
          </p>
        </div>

        <transition name="fade">
          <div
            v-if="shareFeedback"
            class="allies-view__feedback"
            role="status"
            aria-live="polite"
          >
            {{ shareFeedback }}
          </div>
        </transition>
      </header>

      <section class="allies-view__filters" aria-label="filters">
        <label class="allies-view__filter">
          <span>{{ t("allies.filters.search") }}</span>
          <input
            v-model="searchQuery"
            type="search"
            :placeholder="t('allies.filters.search_placeholder')"
            autocomplete="off"
          />
        </label>

        <label class="allies-view__filter">
          <span>{{ t("allies.filters.category") }}</span>
          <select v-model="selectedCategory">
            <option value="">{{ t("allies.filters.category_all") }}</option>
            <option
              v-for="option in categoriesOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>

        <label class="allies-view__filter">
          <span>{{ t("allies.filters.city") }}</span>
          <select v-model="selectedCity">
            <option value="">{{ t("allies.filters.city_all") }}</option>
            <option
              v-for="option in citiesOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </label>
      </section>

      <section class="allies-view__content" aria-live="polite">
        <div v-if="isLoading" class="allies-view__loading">
          <span class="allies-view__spinner" aria-hidden="true" />
          {{ t("allies.loading") }}
        </div>

        <p v-else-if="!allies.length" class="allies-view__empty">
          {{ t("allies.empty") }}
        </p>

        <div v-else class="allies-view__grid">
          <AllyCard
            v-for="item in allies"
            :key="item.id"
            :ally="item"
            @more-info="openAllyModal"
            @share="handleShare"
          />
        </div>

        <nav
          v-if="pagination.last_page > 1"
          class="allies-view__pagination"
          aria-label="pagination"
        >
          <button
            type="button"
            class="allies-view__page-btn"
            :disabled="currentPage === 1"
            @click="goToPreviousPage"
          >
            {{ t("allies.pagination.previous") }}
          </button>
          <span class="allies-view__page-indicator">
            {{ t("allies.pagination.counter", { page: currentPage, total: pagination.last_page }) }}
          </span>
          <button
            type="button"
            class="allies-view__page-btn"
            :disabled="currentPage === pagination.last_page"
            @click="goToNextPage"
          >
            {{ t("allies.pagination.next") }}
          </button>
        </nav>
      </section>
    </div>

    <Teleport to="body">
      <transition name="fade">
        <AllyModal
          v-if="isModalOpen && selectedAlly"
          :ally="selectedAlly"
          :prefill-user="userProfile"
          @close="closeModal"
          @share="handleShare"
          @submitted="handleSubmitted"
          @auth-required="ensureUserProfile"
        />
      </transition>
    </Teleport>
  </section>
</template>

<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import AllyCard from "@/components/alliances/AllyCard.vue";
import AllyModal from "@/components/alliances/AllyModal.vue";
import { shareLink } from "@/utils/share";
import { useAllies } from "@/composables/useAllies";
import { useAuth } from "@/composables/useAuth";
import { trackAllyEvent } from "@/api/metrics";

const {
  allies,
  pagination,
  availableCategories,
  availableCities,
  isLoadingList,
  fetchAlliesList,
  fetchAllyDetail,
  fetchAllyByCode,
} = useAllies();

const { isAuthenticated, userProfile, fetchUserProfile } = useAuth();

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const searchQuery = ref("");
const selectedCategory = ref("");
const selectedCity = ref("");
const currentPage = ref(1);
const isModalOpen = ref(false);
const selectedAlly = ref(null);
const shareFeedback = ref("");
const shareFeedbackTimeout = ref(null);
const isPrefetchingDetail = ref(false);

const categoriesOptions = computed(() =>
  availableCategories.value.map((item) => ({
    label: item.label,
    value: String(item.value),
  }))
);

const citiesOptions = computed(() =>
  availableCities.value.map((item) => ({
    label: item.label,
    value: String(item.value),
  }))
);

const isLoading = computed(() => isLoadingList.value);

const matchesCode = (item, code) => {
  if (!item || !code) {
    return false;
  }
  const normalized = String(code).toLowerCase();
  const candidates = [
    item.code,
    item.slug,
    item.alias,
    item.id,
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());

  return candidates.includes(normalized);
};

const loadAllies = async () => {
  await fetchAlliesList({
    page: currentPage.value,
    category: selectedCategory.value || undefined,
    city: selectedCity.value || undefined,
    search: searchQuery.value || undefined,
  });
  currentPage.value = pagination.value.current_page || currentPage.value;
};

const ensureUserProfile = async () => {
  if (!isAuthenticated.value) {
    return;
  }
  if (userProfile.value) {
    return;
  }
  try {
    await fetchUserProfile();
  } catch (error) {
    console.warn("Unable to fetch user profile", error);
  }
};

const openAllyModal = async (ally, { updateRoute = true } = {}) => {
  if (!ally?.id) {
    return;
  }

  let detail = ally;

  if (!ally.benefit_description && !isPrefetchingDetail.value) {
    try {
      isPrefetchingDetail.value = true;
      const fetched = await fetchAllyDetail(ally.id);
      if (fetched) {
        detail = { ...ally, ...fetched };
      }
    } catch (error) {
      console.warn("Unable to fetch ally detail", error);
    } finally {
      isPrefetchingDetail.value = false;
    }
  }

  selectedAlly.value = detail;
  isModalOpen.value = true;
  await ensureUserProfile();

  if (!updateRoute) {
    return;
  }

  const query = {
    ...route.query,
    open: "1",
  };

  const code = detail.code || detail.slug || detail.alias || detail.id;
  if (code) {
    query.ally = String(code);
  }

  router.replace({ query });
};

const closeModal = () => {
  isModalOpen.value = false;
  selectedAlly.value = null;
  shareFeedback.value = "";
  if (shareFeedbackTimeout.value) {
    clearTimeout(shareFeedbackTimeout.value);
    shareFeedbackTimeout.value = null;
  }

  const query = { ...route.query };
  delete query.open;
  delete query.ally;
  router.replace({ query });
};

const goToPreviousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value -= 1;
    loadAllies();
    window?.scrollTo({ top: 0, behavior: "smooth" });
  }
};

const goToNextPage = () => {
  if (currentPage.value < pagination.value.last_page) {
    currentPage.value += 1;
    loadAllies();
    window?.scrollTo({ top: 0, behavior: "smooth" });
  }
};

const handleSubmitted = () => {
  closeModal();
};

const handleShare = async (ally) => {
  if (!ally) {
    return;
  }

  try {
    const url = new URL(window.location.href);
    const code = ally.code || ally.slug || ally.alias || ally.id;
    if (code) {
      url.searchParams.set("ally", code);
      url.searchParams.set("open", "1");
    }

    const shareMethod = await shareLink({
      url: url.toString(),
      title: ally.name,
      text: t("allies.share_message", { name: ally.name }),
    });

    shareFeedback.value =
      shareMethod === "clipboard"
        ? t("allies.share_copy_feedback")
        : t("allies.share_sent_feedback");

    if (shareFeedbackTimeout.value) {
      clearTimeout(shareFeedbackTimeout.value);
    }
    shareFeedbackTimeout.value = setTimeout(() => {
      shareFeedback.value = "";
    }, 3200);

    await trackAllyEvent(ally.id, "click_share", {
      share_method: shareMethod,
    });
  } catch (error) {
    console.error("Unable to share ally", error);
    shareFeedback.value = t("allies.share_error");
  }
};

const hydrateFromQuery = async () => {
  const { ally: allyCode, open } = route.query;
  if (!allyCode || open !== "1") {
    return;
  }

  if (
    isModalOpen.value &&
    selectedAlly.value &&
    matchesCode(selectedAlly.value, allyCode)
  ) {
    return;
  }

  let ally = allies.value.find((item) => matchesCode(item, allyCode));

  if (!ally) {
    ally = await fetchAllyByCode(allyCode);
    if (ally && !allies.value.some((item) => item?.id === ally.id)) {
      allies.value = [ally, ...allies.value];
    }
  }

  if (ally) {
    await openAllyModal(ally, { updateRoute: false });
  }
};

let searchDebounce = null;

watch(
  () => [selectedCategory.value, selectedCity.value],
  () => {
    currentPage.value = 1;
    loadAllies();
  }
);

watch(
  () => searchQuery.value,
  () => {
    if (searchDebounce) {
      clearTimeout(searchDebounce);
    }
    searchDebounce = setTimeout(() => {
      currentPage.value = 1;
      loadAllies();
    }, 400);
  }
);

watch(
  () => route.query,
  () => {
    hydrateFromQuery();
  }
);

watch(
  () => isAuthenticated.value,
  (isAuth) => {
    if (isAuth) {
      ensureUserProfile();
    }
  },
  { immediate: true }
);

onMounted(async () => {
  await loadAllies();
  await hydrateFromQuery();
});

onBeforeUnmount(() => {
  if (searchDebounce) {
    clearTimeout(searchDebounce);
  }
  if (shareFeedbackTimeout.value) {
    clearTimeout(shareFeedbackTimeout.value);
  }
});
</script>

<style scoped>
.allies-view {
  background: #f3f4f6;
}

.allies-view__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
}

.allies-view__header h1 {
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
  color: #111827;
}

.allies-view__subtitle {
  margin: 6px 0 0;
  color: #6b7280;
  max-width: 640px;
}

.allies-view__feedback {
  background: rgba(16, 185, 129, 0.16);
  color: #065f46;
  padding: 12px 18px;
  border-radius: 12px;
  font-weight: 600;
}

.allies-view__filters {
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  margin-bottom: 32px;
  padding: 24px;
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
}

.allies-view__filter {
  display: flex;
  flex-direction: column;
  gap: 8px;
  color: #374151;
  font-weight: 600;
  font-size: 0.95rem;
}

.allies-view__filter input,
.allies-view__filter select {
  border-radius: 14px;
  border: 1px solid rgba(156, 163, 175, 0.4);
  padding: 12px 16px;
  font-size: 0.95rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.allies-view__filter input:focus,
.allies-view__filter select:focus {
  outline: none;
  border-color: #f97316;
  box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.15);
}

.allies-view__content {
  display: grid;
  gap: 32px;
}

.allies-view__loading {
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  color: #6b7280;
}

.allies-view__spinner {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 3px solid rgba(249, 115, 22, 0.2);
  border-top-color: #f97316;
  animation: spin 0.8s linear infinite;
}

.allies-view__empty {
  text-align: center;
  font-size: 1.05rem;
  color: #6b7280;
  margin: 40px 0;
}

.allies-view__grid {
  display: grid;
  gap: 24px;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
}

.allies-view__pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.allies-view__page-btn {
  border: none;
  border-radius: 999px;
  padding: 10px 20px;
  font-weight: 600;
  cursor: pointer;
  background: rgba(249, 115, 22, 0.12);
  color: #f97316;
  transition: background 0.2s ease, transform 0.2s ease;
}

.allies-view__page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.allies-view__page-btn:not(:disabled):hover {
  background: rgba(249, 115, 22, 0.2);
  transform: translateY(-1px);
}

.allies-view__page-indicator {
  font-weight: 600;
  color: #374151;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .allies-view__header {
    flex-direction: column;
    align-items: stretch;
  }

  .allies-view__filters {
    padding: 20px;
  }
}
</style>
